import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { HtmlVector } from 'record-it-utils/misc/HtmlVector/HtmlVector';
import { StringUtils } from 'record-it-utils/utils/StringUtils/StringUtils';
import { ElementSize } from '../../hooks/useElementSize/useElementSize';
export function useCalculatedTickConfiguration(t0) {
  const $ = _c(13);
  const {
    containerSize,
    tickConfiguration
  } = t0;
  let t1;
  if ($[0] !== containerSize || $[1] !== tickConfiguration.outerTicks) {
    t1 = {
      containerSize,
      ticks: tickConfiguration.outerTicks,
      radiusFactor: 0.8
    };
    $[0] = containerSize;
    $[1] = tickConfiguration.outerTicks;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const outerTicks = useCalculatedTicks(t1);
  let t2;
  if ($[3] !== containerSize || $[4] !== tickConfiguration.innerTicks) {
    t2 = {
      containerSize,
      ticks: tickConfiguration.innerTicks,
      radiusFactor: 0.54
    };
    $[3] = containerSize;
    $[4] = tickConfiguration.innerTicks;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const innerTicks = useCalculatedTicks(t2);
  let t3;
  if ($[6] !== innerTicks || $[7] !== outerTicks) {
    t3 = outerTicks.concat(innerTicks);
    $[6] = innerTicks;
    $[7] = outerTicks;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== innerTicks || $[10] !== outerTicks || $[11] !== t3) {
    t4 = {
      outerTicks,
      innerTicks,
      allTicks: t3
    };
    $[9] = innerTicks;
    $[10] = outerTicks;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
export type UseCalculatedTickConfigurationOptions = {
  containerSize: ElementSize | null;
  tickConfiguration: TickConfiguration;
};
export type TickConfiguration = {
  outerTicks: Array<number>;
  innerTicks: Array<number>;
};
export type CalculatedTick = {
  value: number;
  displayedValue: string;
  position: HtmlVector;
  selectable: boolean;
};
function useCalculatedTicks(t0) {
  const $ = _c(6);
  const {
    containerSize,
    ticks,
    radiusFactor
  } = t0;
  let t1;
  bb0: {
    if (!containerSize) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    const minDimension = Math.min(containerSize.clientWidth, containerSize.clientHeight);
    const radius = minDimension / 2 * radiusFactor;
    let t2;
    if ($[1] !== containerSize.clientHeight || $[2] !== containerSize.clientWidth || $[3] !== radius || $[4] !== ticks) {
      const centerVector = new HtmlVector(containerSize.clientWidth / 2, containerSize.clientHeight / 2);
      t2 = generateTicks(ticks, (value, angle) => {
        const position = new HtmlVector(0, radius * -1).rotate(angle * -1).addVector(centerVector);
        return {
          value,
          displayedValue: StringUtils.padLeft(value.toString(), 2),
          position
        };
      });
      $[1] = containerSize.clientHeight;
      $[2] = containerSize.clientWidth;
      $[3] = radius;
      $[4] = ticks;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    t1 = t2;
  }
  return t1;
}

/**
 * Also generates "filler" ticks for all integer numbers, so even values which are not configured, can be displayed. These fillers are marked as non selectable and shouldn't be rendered.
 */
function generateTicks(configuredTicks: Array<number>, calculateTick: (value: number, angle: number) => Omit<CalculatedTick, 'selectable'>): Array<CalculatedTick> {
  const firstConfiguredTick = configuredTicks[0];
  const lastConfiguredTick = configuredTicks[configuredTicks.length - 1];
  if (firstConfiguredTick === undefined || lastConfiguredTick === undefined) {
    // must be an empty array
    return [];
  }
  const projectedMaxValue = firstConfiguredTick + (lastConfiguredTick - firstConfiguredTick) * configuredTicks.length / (configuredTicks.length - 1);
  const anglePerCount = 360 / (projectedMaxValue - firstConfiguredTick);
  const calculatedTicks: Array<CalculatedTick> = [];
  for (let i = firstConfiguredTick; i < projectedMaxValue; i++) {
    calculatedTicks.push({
      ...calculateTick(i, anglePerCount * (i - firstConfiguredTick)),
      selectable: configuredTicks.includes(i)
    });
  }
  return calculatedTicks;
}
type UseCalculatedTicksOptions = {
  containerSize: ElementSize | null;
  ticks: Array<number>;
  radiusFactor: number;
};